import { Button, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type ServiceStandardItem = { id: string; name: string };
type PositionItem = { id: string; name: string };
type ChecklistItem = { id: string; name: string };
type QCStandardItem = { id: string; name: string };
type KPIItem = { id: string; name: string };

const ServiceStandardMappingFormSchema = z.object({
  serviceStandardId: z.string().min(1, "ກະລຸນາເລືອກມາດຕະຖານບໍລິການ").optional(),
  positionId: z.string().nullable().optional(),
  checklistId: z.string().nullable().optional(),
  qcStandardId: z.string().nullable().optional(),
  kpiId: z.string().nullable().optional(),
});

export type ServiceStandardMappingFormValues = z.infer<
  typeof ServiceStandardMappingFormSchema
>;

export function ServiceStandardMappingForm({
  initialValues,
  onSubmit,
  submitting,
  serviceStandardDepartmentId,
  requireServiceStandard = false,
}: {
  initialValues?: Partial<ServiceStandardMappingFormValues>;
  onSubmit: (values: ServiceStandardMappingFormValues) => void;
  submitting?: boolean;
  serviceStandardDepartmentId?: string | null;
  requireServiceStandard?: boolean;
}) {
  const methods = RHF.useForm<ServiceStandardMappingFormValues>({
    resolver: zodResolver(ServiceStandardMappingFormSchema),
    defaultValues: {
      serviceStandardId: undefined,
      positionId: null,
      checklistId: null,
      qcStandardId: null,
      kpiId: null,
      ...initialValues,
    },
  });

  const departmentId = serviceStandardDepartmentId;

  return (
    <FormRoot<ServiceStandardMappingFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      {requireServiceStandard && (
        <FormInfiniteCombobox<ServiceStandardItem>
          name="serviceStandardId"
          label="ມາດຕະຖານບໍລິການ"
          requiredMark={requireServiceStandard}
          queryKey={["service-standards", "form"]}
          queryFn={(args) =>
            fetchLookupForInfinite(
              `${config.apiUrl}/service-standard/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/service-standard/lookup`, id)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ເລືອກມາດຕະຖານບໍລິການ"
        />
      )}
      <FormInfiniteCombobox<PositionItem>
        name="positionId"
        label="ຕໍາແໜ່ງ"
        queryKey={["service-standard-positions", departmentId || "all"]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/service-standard/positions/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (departmentId) {
            url.searchParams.set("departmentId", departmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/service-standard/positions/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<ChecklistItem>
        name="checklistId"
        label="Checklist"
        queryKey={["service-standard-checklists", departmentId || "all"]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/service-standard/checklists/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (departmentId) {
            url.searchParams.set("departmentId", departmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/service-standard/checklists/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ Checklist (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<QCStandardItem>
        name="qcStandardId"
        label="ມາດຕະຖານ QC"
        queryKey={["service-standard-qc-standards", departmentId || "all"]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/service-standard/qc-standards/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (departmentId) {
            url.searchParams.set("departmentId", departmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/service-standard/qc-standards/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກມາດຕະຖານ QC (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<KPIItem>
        name="kpiId"
        label="KPI"
        queryKey={["service-standard-kpis", departmentId || "all"]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/service-standard/kpis/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (departmentId) {
            url.searchParams.set("departmentId", departmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/service-standard/kpis/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ KPI (ບໍ່ບັງຄັບ)"
        clearable
      />

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={submitting}
          isLoading={submitting}
        >
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}

